import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PasswordRequirements, passwordOk } from "../components/PasswordRequirements";

type ValidState =
  | { phase: "loading" }
  | { phase: "invalid"; message: string }
  | { phase: "valid"; tier: string; email: string | null; note: string | null };

export default function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ValidState>({ phase: "loading" });

  const [companyName, setCompanyName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Lock the email field when the invite pinned a specific address.
  const emailLocked = state.phase === "valid" && !!state.email;

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ phase: "invalid", message: "This invite link is no longer valid." });
      return;
    }
    (async () => {
      try {
        const r = await api.invite.validate(token);
        if (cancelled) return;
        const inviteEmail = r.invite.email ?? null;
        setState({
          phase: "valid",
          tier: r.invite.tier,
          email: inviteEmail,
          note: r.invite.note ?? null,
        });
        if (inviteEmail) setEmail(inviteEmail);
      } catch {
        if (cancelled) return;
        setState({
          phase: "invalid",
          message: "This invite link is no longer valid or has expired.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);

    if (!passwordOk(password)) {
      setError("Please meet all the password requirements below.");
      return;
    }

    setBusy(true);
    try {
      await api.invite.accept(token, {
        company_name: companyName.trim(),
        admin_name: adminName.trim(),
        admin_email: email.trim(),
        admin_password: password,
      });
      setDone(true);
    } catch (e: any) {
      setError(e?.message || "We couldn't create your company. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="brand-lockup login-brand">
          <div className="brand-mark">
            <img src="/brand-mark.png" alt="Alief Dispute Hub" />
          </div>
          <div>
            <strong className="brand-wordmark">Alief Dispute Hub</strong>
            <span>Analyze Smarter. Dispute Better.</span>
          </div>
        </div>

        {state.phase === "loading" && (
          <p role="status" aria-live="polite">
            Checking your invite…
          </p>
        )}

        {state.phase === "invalid" && (
          <>
            <h1 id="join-heading">This invite link is no longer valid</h1>
            <p>
              The link may have expired or already been used. Ask whoever invited
              you to send a fresh invite.
            </p>
            <p>
              <Link to="/login">Back to sign-in</Link>
            </p>
          </>
        )}

        {state.phase === "valid" && done && (
          <>
            <h1 id="join-heading">Your company is ready</h1>
            <p role="status" aria-live="polite">
              {companyName.trim() || "Your company"} has been created. You can sign
              in now.
            </p>
            <Link to="/login" className="primary-button wide-button">
              Sign in
            </Link>
          </>
        )}

        {state.phase === "valid" && !done && (
          <>
            <h1 id="join-heading">Set up your company</h1>
            {state.note && <p>{state.note}</p>}
            <form
              onSubmit={submit}
              className="stacked-form"
              aria-labelledby="join-heading"
            >
              <label className="form-field">
                <span>Company name</span>
                <input
                  type="text"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  aria-required="true"
                />
              </label>

              <label className="form-field">
                <span>Your name</span>
                <input
                  type="text"
                  autoComplete="name"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  required
                  aria-required="true"
                />
              </label>

              <label className="form-field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-required="true"
                  readOnly={emailLocked}
                  aria-readonly={emailLocked || undefined}
                />
              </label>

              <label className="form-field">
                <span>Password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-required="true"
                  minLength={8}
                />
                <PasswordRequirements password={password} />
              </label>

              {error && (
                <p className="error-text" role="alert">
                  {error}
                </p>
              )}

              <button
                className="primary-button wide-button"
                disabled={busy}
              >
                {busy ? "Creating your company…" : "Create company"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
